import { TeacherRoomManager } from "@/manager/room/teacher.manager";
import { RoomModel, TeachingMode } from "@/models";
import { GetterTree } from "vuex";
import { HelperState, ParamsToJoinCurSessionInterface, StudentState, TeacherState } from "../interface";
import { ClassAction } from "../student/state";
import { TeacherRoomState } from "./state";
import { defineVuexName, MODULES } from "@/store/utils";

const getters: GetterTree<TeacherRoomState, any> = {
  [defineVuexName(MODULES.TEACHER_ROOM.GETTERS.GET_IS_SOCKET_CONNECTED)](state: TeacherRoomState): boolean {
    return state.isConnected;
  },
  [defineVuexName(MODULES.TEACHER_ROOM.GETTERS.GET_IS_REALTIME_READY)](state: TeacherRoomState): boolean {
    return state.isRealtimeReady;
  },
  [defineVuexName(MODULES.TEACHER_ROOM.GETTERS.GET_CAN_SEND_REALTIME_ACTION)](state: TeacherRoomState): boolean {
    return state.isConnected && state.isRealtimeReady;
  },
  [defineVuexName(MODULES.TEACHER_ROOM.GETTERS.GET_INFO)](state: TeacherRoomState): RoomModel {
    return state.info as RoomModel;
  },
  [defineVuexName(MODULES.TEACHER_ROOM.GETTERS.GET_ERROR)](state: TeacherRoomState): any {
    return state.error;
  },
  [defineVuexName(MODULES.TEACHER_ROOM.GETTERS.GET_STUDENTS)](state: TeacherRoomState): Array<StudentState> {
    return state.students;
  },
  [defineVuexName(MODULES.TEACHER_ROOM.GETTERS.GET_TEACHER)](state: TeacherRoomState): TeacherState | undefined {
    return state.teacher as TeacherState;
  },
  [defineVuexName(MODULES.TEACHER_ROOM.GETTERS.GET_ROOM_MANAGER)](state: TeacherRoomState): TeacherRoomManager {
    return state.manager as TeacherRoomManager;
  },
  [defineVuexName(MODULES.TEACHER_ROOM.GETTERS.GET_TEACHING_MODE)](state: TeacherRoomState): TeachingMode {
    return state.teachingMode;
  },
  [defineVuexName(MODULES.TEACHER_ROOM.GETTERS.GET_CLASS_ACTION)](state: TeacherRoomState): ClassAction {
    return state.classAction;
  },
  [defineVuexName(MODULES.TEACHER_ROOM.GETTERS.GET_STUDENT_MODE_ONE_ID)](state: TeacherRoomState): string {
    return state.idOne;
  },
  [defineVuexName(MODULES.TEACHER_ROOM.GETTERS.GET_STUDENT_MODE_ONE_WITH_ANOTHER_TEACHER_ID)](state: TeacherRoomState): string {
    return state.idOneWithAnotherTeacher;
  },
  [defineVuexName(MODULES.TEACHER_ROOM.GETTERS.GET_TEACHER_OR_HELPER_ONE_TO_ONE)](state: TeacherRoomState): boolean {
    return !!state.idOne || !!state.idOneWithAnotherTeacher;
  },
  [defineVuexName(MODULES.TEACHER_ROOM.GETTERS.GET_ONE_TO_ONE_STUDENT_IDS)](state: TeacherRoomState): string[] {
    const studentIds = [];
    if (state.idOne) {
      studentIds.push(state.idOne);
    }
    if (state.idOneWithAnotherTeacher) {
      studentIds.push(state.idOneWithAnotherTeacher);
    }
    return studentIds;
  },
  [defineVuexName(MODULES.TEACHER_ROOM.GETTERS.GET_NOT_ONE_TO_ONE_STUDENT_IDS)](state: TeacherRoomState): string[] {
    return state.students.filter((st) => st.id !== state.idOne && st.id !== state.idOneWithAnotherTeacher).map((s) => s.id);
  },
  [defineVuexName(MODULES.TEACHER_ROOM.GETTERS.GET_STUDENT_MODE_ONE_WITH_ANOTHER_TEACHER)](state: TeacherRoomState): StudentState | undefined {
    return state.students.find((s) => s.id === state.idOneWithAnotherTeacher);
  },
  [defineVuexName(MODULES.TEACHER_ROOM.GETTERS.GET_SPEAKING_USERS)](state: TeacherRoomState): Array<string> {
    return state.speakingUsers;
  },
  [defineVuexName(MODULES.TEACHER_ROOM.GETTERS.GET_IS_DISCONNECTED)](state: TeacherRoomState): boolean {
    return state.isDisconnected;
  },
  [defineVuexName(MODULES.TEACHER_ROOM.GETTERS.GET_LIST_STUDENT_LOW_BAND_WIDTH)](state: TeacherRoomState): string[] {
    return state.listStudentLowBandWidth;
  },
  [defineVuexName(MODULES.TEACHER_ROOM.GETTERS.GET_CURRENT_LESSON)](state: TeacherRoomState): number {
    return state.currentLesson;
  },
  [defineVuexName(MODULES.TEACHER_ROOM.GETTERS.GET_CURRENT_UNIT)](state: TeacherRoomState): number {
    return state.currentUnit;
  },
  [defineVuexName(MODULES.TEACHER_ROOM.GETTERS.GET_IS_SHOW_WHITE_BOARD)](state: TeacherRoomState): boolean {
    return state.isShowWhiteboard;
  },
  [defineVuexName(MODULES.TEACHER_ROOM.GETTERS.GET_IS_TEACHER_USE_ONLY)](state: TeacherRoomState): boolean {
    return state.isTeacherUseOnly;
  },
  [defineVuexName(MODULES.TEACHER_ROOM.GETTERS.GET_MEDIA_STATE)](state: TeacherRoomState): boolean {
    return state.mediaState;
  },
  [defineVuexName(MODULES.TEACHER_ROOM.GETTERS.GET_HELPER_ID)](state: TeacherRoomState): string | undefined {
    return state.helper?.id;
  },
  [defineVuexName(MODULES.TEACHER_ROOM.GETTERS.GET_HELPER_INFO)](state: TeacherRoomState): HelperState | undefined {
    return state.helper;
  },
  helperVideoStatus(state: TeacherRoomState): boolean {
    return state.helper?.isVideoShownByTeacher ?? false;
  },
  [defineVuexName(MODULES.TEACHER_ROOM.GETTERS.GET_HELPER_AVATAR)](state: TeacherRoomState): string {
    return state.helper?.avatar ?? "";
  },
  [defineVuexName(MODULES.TEACHER_ROOM.GETTERS.GET_PARAMS_TO_JOIN_CUR_SESSION)](state: TeacherRoomState): ParamsToJoinCurSessionInterface {
    return state.paramsToJoinCurSession;
  },
  [defineVuexName(MODULES.TEACHER_ROOM.GETTERS.GET_TEACHER_IS_DISCONNECTED)](state: TeacherRoomState): boolean {
    return state.teacherIsDisconnected;
  },
  // Note: In the teacher/helper state
  // If logged in with the role of Teacher:
  // idOne will be the ID of the student with whom the teacher is in a one-on-one mode;
  // idOneWithAnotherTeacher will be the ID of the student with whom the helper is in a one-on-one mode.
  // If logged in with the role of Helper:
  // idOne will be the ID of the student with whom the helper is in a one-on-one mode;
  // idOneWithAnotherTeacher will be the ID of the student with whom the teacher is in a one-on-one mode.
  [defineVuexName(MODULES.TEACHER_ROOM.GETTERS.GET_ONE_TO_ONE_STUDENT_ID)](s: TeacherRoomState) {
    return s.idOne;
  },
  [defineVuexName(MODULES.TEACHER_ROOM.GETTERS.GET_HELPER_ONE_TO_ONE_STUDENT_ID)](s: TeacherRoomState) {
    return s.idOneWithAnotherTeacher;
  },
  [defineVuexName(MODULES.TEACHER_ROOM.GETTERS.GET_IS_TEACHER_ALLOW_STUDENT_SEE_HELPER_VIDEO)](s: TeacherRoomState): boolean {
    return s.helper?.isVideoShownByTeacher ?? false;
  },
};

export default getters;
